// ---------------------------------------------------------------------------
// Broods on the Hive
// ---------------------------------------------------------------------------
//
// A Brood (internally still "Base"/fort in places) has a body on the Hive, not
// only in its corridor. Corridor units are aircraft — they cannot be touched
// by Hive damage; the Brood's body can. A Brood is NOT a structure: it lives
// in the Hive's own `broods` map and its cells carry broodId, so it never
// counts toward kills, pays yield, hosts rockets or drones, or draws intel.
//
// Shape: a horizontal bar of 3 subplots spanning the corridor's full 3-cell
// width: the nest in the centre and a drone on each outer cell. Once both
// drones are cratered the brood is dead, nest or no nest (see
// isBroodDead). UNDER CONSTRUCTION it is far more fragile:
// a single destroyed cell collapses the whole thing (see subplotDamage's
// collapse pass). The center is pinned to the corridor's MIDDLE subplot
// column; the player picks the row during the Build Base flow.
//
// The corridor board stays the combat source of truth (fortCount /
// fortInConstruction / fortProgress / baseCenterS); the Brood this module
// manages is its body on the Hive — the thing craters can
// reach. computeAttacks keeps the two in lockstep deterministically on both
// clients (declared builds merge at reveal; deaths reconcile after battles
// and craters).
//
// Broods are visible to the opponent from the reveal of the round their
// construction was submitted.

import { subIndex, subCoords, SUB_COLS, SUB_ROWS } from './subplotGrid.js'

// The corridor's middle subplot column — the only column a Base center may
// occupy (the horizontal arm then spans the corridor exactly).
export function baseCenterColumn(col) {
    return col * 3 + 1
}

// The 3-cell bar footprint centred on `centerS`, or null when an end would
// fall off the grid. Order: center first, then W/E ends (footprint[0] is the
// center everywhere downstream). Any row works — the bar has no vertical arms.
export function baseFootprint(centerS) {
    if (centerS == null) return null
    const { sc, sr } = subCoords(centerS)
    if (sc < 1 || sc > SUB_COLS - 2 || sr < 0 || sr > SUB_ROWS - 1) return null
    return [
        centerS,
        subIndex(sc - 1, sr),
        subIndex(sc + 1, sr),
    ]
}

// A Brood is dead once both its drones are cratered (its outer cells), nest
// or no nest. EXCEPTIONS:
//   - UNDER CONSTRUCTION it is scaffolding, not armor: ONE destroyed
//     footprint cell kills it.
//   - A legacy hatchling on a standing nest (wars before rules v4) is a third
//     drone and keeps it alive.
// The BROOD dies, not the ground: its surviving cells are never cratered (the
// crater keeps its own bounds); they revert to open land when the corpse is
// swept (sweepDeadBroods).
export function isBroodDead(homefront, id) {
    const b = homefront?.broods?.[id]
    if (!b?.footprint?.length) return false
    const destroyed = (s) => !!homefront.subplots[s]?.destroyed
    if (b.underConstruction) return b.footprint.some(destroyed)
    const [nest, ...ends] = b.footprint
    const hatchlingLives = b.egg === 'hatched' && !destroyed(nest)
    return ends.every(destroyed) && !hatchlingLives
}

// The corridor's standing Broods (live or under construction) — corridors can
// hold ANY number; only Hive real estate limits them. A Brood whose last cell
// was destroyed stays in `broods` as a corpse (its craters keep their look)
// but no longer counts among the corridor's Broods.
export function broodsFor(homefront, col) {
    const out = []
    if (!homefront?.broods) return out
    for (const id of Object.keys(homefront.broods)) {
        const b = homefront.broods[id]
        if (b.corridor === col && !isBroodDead(homefront, id)) out.push(b)
    }
    return out
}

// Back-compat singular (first standing Brood, or null).
export function broodFor(homefront, col) {
    return broodsFor(homefront, col)[0] ?? null
}

// Placement validity for one center cell: footprint fully on the grid, center
// pinned to the corridor's middle column, every cell standing (not destroyed),
// free of structures and other Broods.
export function canPlaceBase(homefront, col, centerS) {
    if (!homefront?.subplots) return false
    const { sc } = subCoords(centerS)
    if (sc !== baseCenterColumn(col)) return false
    const footprint = baseFootprint(centerS)
    if (!footprint) return false
    for (const s of footprint) {
        const cell = homefront.subplots[s]
        if (!cell || cell.destroyed || cell.structureId != null || cell.broodId != null) return false
    }
    return true
}

// Every legal Base center in the corridor — the Build Base flow's tap targets.
export function eligibleBaseCenters(homefront, col) {
    const out = []
    if (!homefront?.subplots) return out
    const sc = baseCenterColumn(col)
    for (let sr = 0; sr <= SUB_ROWS - 1; sr++) {
        const centerS = subIndex(sc, sr)
        if (canPlaceBase(homefront, col, centerS)) out.push(centerS)
    }
    return out
}

// Stamps a Brood onto the Hive. MUTATES the given homefront (callers in the
// resolution path work on already-cloned homefronts). Returns the Brood, or
// null when the placement is no longer legal (defensive — the build was
// validated at placement time).
export function addBrood(homefront, col, centerS, round) {
    if (!canPlaceBase(homefront, col, centerS)) return null
    // centerS in the id keeps several same-round builds in one corridor
    // distinct (footprints can't overlap, so centers are unique).
    const id = `base-c${col}-s${centerS}-r${round}`
    const footprint = baseFootprint(centerS)
    for (const s of footprint) {
        homefront.subplots[s].broodId = id
    }
    if (!homefront.broods) homefront.broods = {}
    const b = { id, footprint, corridor: col, underConstruction: true }
    homefront.broods[id] = b
    return b
}

// Marks one Brood operational by its center cell (construction finished):
// its first egg appears on the nest. Mutates.
export function matureBroodAt(homefront, col, centerS) {
    const b = broodsFor(homefront, col).find(x => x.footprint[0] === centerS)
    if (!b) return
    b.underConstruction = false
    if (!b.egg) b.egg = 'egg'
}

// ── The brood ───────────────────────────────────────────────────────────────
// A Base is a BROOD. Its two funding drones landed on the OUTER cells of the
// bar (footprint[1], footprint[2] — the pad cells) and fight from there; the
// CENTRE cell is the nest. Once the brood matures an EGG sits on the nest:
// the airdrop in the making, launched from that cell as the drop zone's
// delivery each round, a fresh one laid for the next. st.egg:
//   undefined  under construction — no egg yet
//   'egg'      mature and intact: launchable, and it lays again next round
//   'gone'     the nest was cratered, or one of the brood's drones was (the
//              egg dies with its parent; it does not hatch): no airdrop
//              from this brood again
//   'hatched'  legacy only (wars resolved before rules v4): a cratered
//              drone used to hatch the egg into a drone on the nest. No
//              longer produced; still counted where an old Grid holds it
// Strength is the live drones: live outer cells (plus a legacy hatchling
// while the nest stands). Death is when no drone is left: both outer cells
// cratered (isBroodDead).
// A brood that FIRES takes its egg with it: the egg dies with the brood and
// never launches (resolveRound blocks fired broods' airdrops).
// broodStrength counts the drones ON THE BAR whatever the construction
// state — a brood under construction is worth 0 in battle, but that is the
// ledger's `mature` flag (gameLogic.baseStrength), not a missing drone: the
// ledger is mirrored from the Grid BEFORE the Grid flips a maturing brood
// operational, and the hp it stores must already be the two drones.
export function broodEndCells(st) {
    return (st?.footprint ?? []).slice(1, 3)
}
export function broodStrength(homefront, st) {
    if (!st || !homefront?.subplots) return 0
    const live = (s) => !homefront.subplots[s]?.destroyed
    const ends = broodEndCells(st).filter(live).length
    const hatchling = st.egg === 'hatched' && live(st.footprint[0]) ? 1 : 0
    return ends + hatchling
}
// Whether this brood's egg can still fly as an airdrop.
export function eggLaunchable(homefront, st) {
    return !!st && !st.underConstruction && st.egg === 'egg' && !homefront?.subplots?.[st.footprint[0]]?.destroyed
}
// The egg's answer to fresh craters, run right after each strike lands: a
// cratered nest OR a cratered drone kills the egg ('gone') — it dies with
// its parent and never launches or hatches. Mutates; returns what changed
// ([{ centerS, egg }]) so the reveal can show it.
export function hatchBroods(homefront) {
    const changed = []
    for (const b of Object.values(homefront?.broods ?? {})) {
        if (b.underConstruction || b.egg !== 'egg') continue
        if (b.footprint.some(s => homefront.subplots[s]?.destroyed)) {
            b.egg = 'gone'
            changed.push({ centerS: b.footprint[0], egg: 'gone' })
        }
    }
    return changed
}

// Removes one standing Brood from the Hive — the death that comes from the
// CORRIDOR side (the Brood fired, or a legacy clear), not from damage. Cells
// revert to open ground; destroyed cells keep their craters. Mutates.
export function removeBroodById(homefront, id) {
    const b = homefront?.broods?.[id]
    if (!b) return false
    for (const s of b.footprint) {
        const cell = homefront.subplots[s]
        if (cell && cell.broodId === id) cell.broodId = null
    }
    delete homefront.broods[id]
    return true
}

// Removes every standing Brood in the corridor. Returns whether any was removed.
export function removeBroods(homefront, col) {
    const bs = broodsFor(homefront, col)
    for (const b of bs) removeBroodById(homefront, b.id)
    return bs.length > 0
}

// Sweeps the corpses of Broods killed BY DAMAGE off the Hive: a Brood is not
// permanent, so once its kill has been shown (its X's during the clash) no
// dead marks linger — the cells a crater reached stay plain craters, and the
// rest (a dead brood's standing nest, a collapsed CONSTRUCTION's surviving
// cells) revert to open, undamaged land. Run on the FINAL post-crater
// homefronts only; the per-clash animation snapshots keep the corpse so the
// kill reads. Mutates. Returns true when anything was swept.
export function sweepDeadBroods(homefront) {
    let swept = false
    for (const id of Object.keys(homefront?.broods ?? {})) {
        if (!isBroodDead(homefront, id)) continue
        for (const s of homefront.broods[id].footprint) {
            const cell = homefront.subplots[s]
            if (cell && cell.broodId === id) cell.broodId = null
        }
        delete homefront.broods[id]
        swept = true
    }
    return swept
}
